import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, ShieldCheck } from 'lucide-react';
import { apiDel, apiGet, apiPost, apiPut } from '../../lib/api.js';
import { USE_FOR_OPTIONS, emptyUseFor, fmtDashedPhone, useForChanged } from '../../lib/smsTwoFactor.js';

const errMsg = (e, fallback) => e?.response?.data?.error || e?.message || fallback;
const field = 'w-full bg-civix-panelLight border border-civix-border rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple transition';

/**
 * "Setup Two Factor Authentication (2FA)" — SMS phone verification, the
 * "Use 2FA for" preferences (all unchecked by default) and the danger zone.
 * @returns {JSX.Element}
 */
export default function SmsTwoFactorSection() {
  const [info, setInfo] = useState({ phone: '', verified: false, useFor: emptyUseFor() });
  const [draft, setDraft] = useState(emptyUseFor());
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');

  const apply = (r) => {
    const next = { phone: r.phone || '', verified: !!r.verified, useFor: { ...emptyUseFor(), ...(r.useFor || {}) } };
    setInfo(next);
    setDraft(next.useFor);
  };

  useEffect(() => {
    apiGet('/auth/2fa/sms').then(apply).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  async function run(fn, fallback) {
    setBusy(true);
    try { return await fn(); } catch (e) { toast.error(errMsg(e, fallback)); return null; } finally { setBusy(false); }
  }

  const sendCode = async () => {
    const r = await run(() => apiPost('/auth/2fa/sms/send-code', { phone: phoneInput }), 'Could not send the code');
    if (r) { setCodeSent(true); toast.success('Verification code sent'); }
  };
  const verify = async () => {
    const r = await run(() => apiPost('/auth/2fa/sms/verify', { code }), 'Verification failed');
    if (r) { apply(r); setCode(''); setCodeSent(false); setPhoneInput(''); toast.success('Phone number verified'); }
  };
  const save = async () => {
    const r = await run(() => apiPut('/auth/2fa/sms/preferences', draft), 'Could not save');
    if (r) { apply(r); toast.success('2FA preferences saved'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete your 2FA phone number? Text-message 2FA will be turned off for login, lead exports and password resets.')) return;
    const r = await run(() => apiDel('/auth/2fa/sms'), 'Could not delete the phone number');
    if (r) { apply(r); toast.success('2FA phone number deleted'); }
  };

  return (
    <section className="panel p-6 space-y-5" data-testid="sms-2fa">
      <header className="flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-civix-purple/20 border border-civix-purple/40 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-civix-purpleSoft" />
        </span>
        <h2 className="text-xl font-display font-semibold">Setup Two Factor Authentication (2FA)</h2>
      </header>

      {!loaded ? null : info.verified ? (
        <>
          <p className="text-sm text-white/70">You have a phone number verified for 2 factor authentication.</p>
          <div className="flex items-center gap-3">
            <label className="relative block w-full max-w-[240px]">
              <span className="absolute -top-2 left-3 px-1 bg-civix-panel text-[11px] text-white/50">2FA Phone Number</span>
              <input readOnly value={fmtDashedPhone(info.phone)} className={field} aria-label="2FA Phone Number" />
            </label>
            <Check className="w-5 h-5 text-emerald-400" aria-label="Verified" />
          </div>

          <fieldset className="space-y-2.5">
            <legend className="text-sm text-white/80 mb-2">Use 2FA for:</legend>
            {USE_FOR_OPTIONS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 text-sm cursor-pointer w-fit">
                <input
                  type="checkbox"
                  className="w-5 h-5 accent-civix-purple"
                  checked={!!draft[key]}
                  data-testid={`sms2fa-${key}`}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </fieldset>

          <button
            type="button"
            onClick={save}
            disabled={busy || !useForChanged(draft, info.useFor)}
            className="px-6 py-2.5 rounded-md text-sm font-bold tracking-wide bg-civix-purple text-white hover:bg-civix-purpleDeep disabled:bg-white/10 disabled:text-white/35 disabled:cursor-not-allowed transition cursor-pointer"
          >
            SAVE
          </button>

          <div className="border-t border-civix-border pt-4 space-y-1">
            <div className="text-sm font-semibold">Danger zone</div>
            <p className="text-xs text-red-300">WARNING: Actions in this section are irreversible</p>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="px-5 py-2.5 rounded-md text-sm font-bold tracking-wide border border-red-400/60 text-red-300 hover:bg-red-500/10 disabled:opacity-50 transition cursor-pointer"
              >
                DELETE PHONE NUMBER
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-3 max-w-md">
          <p className="text-sm text-white/70">Add a mobile number to receive 2FA codes by text message.</p>
          <input
            type="tel"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="2FA phone number"
            className={field}
            aria-label="2FA phone number"
          />
          {codeSent && (
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              inputMode="numeric"
              className={`${field} font-mono tracking-widest`}
              aria-label="6-digit code"
            />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={sendCode} disabled={busy || phoneInput.replace(/\D/g, '').length < 10} className="btn btn-primary disabled:opacity-50">
              {codeSent ? 'RESEND CODE' : 'SEND CODE'}
            </button>
            {codeSent && (
              <button type="button" onClick={verify} disabled={busy || code.length !== 6} className="btn btn-primary disabled:opacity-50">VERIFY</button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
